import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ApiClientError } from '../api/http'
import { Avatar, Button, Card, Field, I, Pill, MOBILE_SINGLE_COLUMN, TEXT_WRAP_STYLE, mobileButtonStyle } from '../components/ui'
import { useIsMobileViewport } from '../hooks/useIsMobileViewport'
import { cancelTeamInvitation, getTeam, inviteTeamMember, removeTeamMember } from '../team/teamApi'
import type { TeamInvitation, TeamMember, TeamResponse } from '../team/types'

interface TeamPageProps {
  accessToken: string
}

function formatDate(value: string | null | undefined, locale: string, fallback: string): string {
  if (!value) return fallback
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return fallback
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(date)
}

function teamErrorKey(error: unknown, fallback: string): string {
  if (!(error instanceof ApiClientError)) return fallback
  if (error.code === 'TEAM_EMAIL_ALREADY_USER') return 'team.errors.emailAlreadyUser'
  if (error.code === 'OWNER_REQUIRED') return 'team.errors.ownerRequired'
  return fallback
}

function MobileMeta({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: 'grid', gap: 4, minWidth: 0 }}>
      <div style={{ color: 'var(--fg-subtle)', fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ color: 'var(--fg)', fontSize: 13, ...TEXT_WRAP_STYLE }}>{children}</div>
    </div>
  )
}

export function TeamPage({ accessToken }: TeamPageProps) {
  const { t, i18n } = useTranslation()
  const isMobile = useIsMobileViewport()
  const [team, setTeam] = useState<TeamResponse | null>(null)
  const [teamLoadedFor, setTeamLoadedFor] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviteFullName, setInviteFullName] = useState('')
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteSubmitting, setInviteSubmitting] = useState(false)
  const [actionBusyId, setActionBusyId] = useState<string | null>(null)

  const loadTeam = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setTeam(await getTeam(accessToken))
      setTeamLoadedFor(accessToken)
    } catch (caught) {
      setError(t(teamErrorKey(caught, 'team.errors.load')))
      setTeamLoadedFor(accessToken)
    } finally {
      setLoading(false)
    }
  }, [accessToken, t])

  useEffect(() => {
    let cancelled = false

    getTeam(accessToken)
      .then(response => {
        if (cancelled) return
        setTeam(response)
      })
      .catch(caught => {
        if (cancelled) return
        setError(t(teamErrorKey(caught, 'team.errors.load')))
      })
      .finally(() => {
        if (cancelled) return
        setTeamLoadedFor(accessToken)
        setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [accessToken, t])

  async function handleInvite(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError(null)

    const fullName = inviteFullName.trim()
    const email = inviteEmail.trim()
    if (!fullName || !email) {
      setError(t('team.errors.inviteFields'))
      return
    }

    setInviteSubmitting(true)
    try {
      await inviteTeamMember(accessToken, { fullName, email })
      setInviteFullName('')
      setInviteEmail('')
      setInviteOpen(false)
      await loadTeam()
    } catch (caught) {
      setError(t(teamErrorKey(caught, 'team.errors.invite')))
    } finally {
      setInviteSubmitting(false)
    }
  }

  async function handleRemove(member: TeamMember) {
    if (!window.confirm(t('team.confirm.remove', { name: member.fullName }))) return
    const busyId = `member-${member.id}`
    setActionBusyId(busyId)
    setError(null)
    try {
      await removeTeamMember(accessToken, member.id)
      await loadTeam()
    } catch (caught) {
      setError(t(teamErrorKey(caught, 'team.errors.remove')))
    } finally {
      setActionBusyId(null)
    }
  }

  async function handleCancel(invitation: TeamInvitation) {
    if (!window.confirm(t('team.confirm.cancelInvite', { email: invitation.email }))) return
    const busyId = `invite-${invitation.id}`
    setActionBusyId(busyId)
    setError(null)
    try {
      await cancelTeamInvitation(accessToken, invitation.id)
      await loadTeam()
    } catch (caught) {
      setError(t(teamErrorKey(caught, 'team.errors.cancelInvite')))
    } finally {
      setActionBusyId(null)
    }
  }

  const pageLoading = loading || teamLoadedFor !== accessToken
  const members = team?.members ?? []
  const invitations = team?.invitations ?? []
  const emptyValue = t('team.empty.notAvailable')

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <div style={{ display: isMobile ? 'grid' : 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'flex-start', gap: 16 }}>
        <p style={{ fontSize: 13.5, color: 'var(--fg-muted)', margin: 0, maxWidth: 620, lineHeight: 1.55, ...TEXT_WRAP_STYLE }}>
          {t('team.description')}
        </p>
        <Button kind="primary" size="sm" icon={I.plus} onClick={() => setInviteOpen(true)} style={mobileButtonStyle(isMobile)}>
          {t('team.invite')}
        </Button>
      </div>

      {inviteOpen && (
        <Card padding={20}>
          <form onSubmit={handleInvite} style={{ display: 'grid', gap: 14 }}>
            <div style={{ display: 'grid', gap: 4 }}>
              <h3 style={{ margin: 0, color: 'var(--fg)', fontSize: 17 }}>{t('team.inviteForm.title')}</h3>
              <p style={{ margin: 0, color: 'var(--fg-muted)', fontSize: 13, lineHeight: 1.5 }}>{t('team.inviteForm.helper')}</p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? MOBILE_SINGLE_COLUMN : 'minmax(0, 1fr) minmax(0, 1fr)', gap: 12 }}>
              <Field label={t('team.inviteForm.fullName')} placeholder={t('team.inviteForm.fullNamePlaceholder')} value={inviteFullName} onChange={event => setInviteFullName(event.target.value)} />
              <Field label={t('team.inviteForm.email')} type="email" placeholder={t('team.inviteForm.emailPlaceholder')} value={inviteEmail} onChange={event => setInviteEmail(event.target.value)} dir="ltr" />
            </div>
            <div style={{ display: isMobile ? 'grid' : 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button kind="secondary" size="sm" onClick={() => setInviteOpen(false)} disabled={inviteSubmitting} style={mobileButtonStyle(isMobile)}>
                {t('team.inviteForm.cancel')}
              </Button>
              <Button type="submit" kind="primary" size="sm" disabled={inviteSubmitting} style={mobileButtonStyle(isMobile)}>
                {inviteSubmitting ? t('team.inviteForm.submitting') : t('team.inviteForm.submit')}
              </Button>
            </div>
          </form>
        </Card>
      )}

      {error && (
        <div style={{ padding: '10px 12px', borderRadius: 10, background: 'var(--danger-100)', color: 'var(--danger)', fontSize: 13, lineHeight: 1.45 }}>
          {error}
        </div>
      )}

      {pageLoading ? (
        <Card>
          <div style={{ color: 'var(--fg-muted)', fontSize: 14, fontWeight: 700 }}>{t('team.loading')}</div>
        </Card>
      ) : (
        <>
          <section style={{ display: 'grid', gap: 10 }}>
            <h3 style={{ margin: 0, color: 'var(--fg)', fontSize: 17 }}>{t('team.sections.members')}</h3>
            <Card padding={0}>
              {isMobile ? (
                <div style={{ display: 'grid' }}>
                  {members.length === 0 ? (
                    <div style={{ padding: '18px 22px', color: 'var(--fg-muted)', fontSize: 13 }}>{t('team.empty.members')}</div>
                  ) : members.map(member => (
                    <div key={member.id} style={{ display: 'grid', gap: 12, padding: 16, borderTop: '1px solid var(--border-subtle)' }}>
                      <div style={{ display: 'flex', gap: 12, alignItems: 'center', minWidth: 0 }}>
                        <Avatar name={member.fullName} size={36} />
                        <div style={TEXT_WRAP_STYLE}>
                          <div style={{ fontWeight: 700, color: 'var(--fg)' }}>{member.fullName}</div>
                          <div style={{ fontSize: 12, color: 'var(--fg-subtle)', overflowWrap: 'anywhere' }}>{member.email}</div>
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: MOBILE_SINGLE_COLUMN, gap: 10 }}>
                        <MobileMeta label={t('team.table.access')}>
                          <Pill tone={member.owner ? 'brand' : 'info'} size="sm">
                            {member.owner ? t('team.access.owner') : t('team.access.full')}
                          </Pill>
                        </MobileMeta>
                        <MobileMeta label={t('team.table.status')}>
                          <Pill tone="success" dot size="sm">{t('team.status.active')}</Pill>
                        </MobileMeta>
                        <MobileMeta label={t('team.table.lastSeen')}>
                          {formatDate(member.lastLoginAt, i18n.language, t('team.empty.lastSeen'))}
                        </MobileMeta>
                      </div>
                      {member.removable ? (
                        <Button kind="danger" size="xs" icon={I.trash} disabled={actionBusyId === `member-${member.id}`} onClick={() => void handleRemove(member)} style={mobileButtonStyle(isMobile)}>
                          {t('team.actions.remove')}
                        </Button>
                      ) : (
                        <span style={{ color: 'var(--fg-subtle)', fontSize: 12 }}>{emptyValue}</span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: 'var(--ink-50)' }}>
                      {[t('team.table.member'), t('team.table.access'), t('team.table.status'), t('team.table.lastSeen'), t('team.table.actions')].map(header => (
                        <th key={header} style={{ textAlign: 'start', padding: '11px 22px', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-subtle)' }}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {members.length === 0 ? (
                      <tr>
                        <td colSpan={5} style={{ padding: '18px 22px', color: 'var(--fg-muted)' }}>{t('team.empty.members')}</td>
                      </tr>
                    ) : members.map(member => (
                      <tr key={member.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                        <td style={{ padding: '14px 22px' }}>
                          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                            <Avatar name={member.fullName} size={32} />
                            <div>
                              <div style={{ fontWeight: 600, color: 'var(--fg)' }}>{member.fullName}</div>
                              <div style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>{member.email}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: '14px 22px' }}>
                          <Pill tone={member.owner ? 'brand' : 'info'} size="sm">
                            {member.owner ? t('team.access.owner') : t('team.access.full')}
                          </Pill>
                        </td>
                        <td style={{ padding: '14px 22px' }}>
                          <Pill tone="success" dot size="sm">{t('team.status.active')}</Pill>
                        </td>
                        <td style={{ padding: '14px 22px', color: 'var(--fg-muted)', fontSize: 12.5 }}>
                          {formatDate(member.lastLoginAt, i18n.language, t('team.empty.lastSeen'))}
                        </td>
                        <td style={{ padding: '14px 22px', textAlign: 'end' }}>
                          {member.removable ? (
                            <Button kind="danger" size="xs" icon={I.trash} disabled={actionBusyId === `member-${member.id}`} onClick={() => void handleRemove(member)}>
                              {t('team.actions.remove')}
                            </Button>
                          ) : (
                            <span style={{ color: 'var(--fg-subtle)', fontSize: 12 }}>{emptyValue}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
          </section>

          <section style={{ display: 'grid', gap: 10 }}>
            <h3 style={{ margin: 0, color: 'var(--fg)', fontSize: 17 }}>{t('team.sections.pendingInvites')}</h3>
            <Card padding={0}>
              {isMobile ? (
                <div style={{ display: 'grid' }}>
                  {invitations.length === 0 ? (
                    <div style={{ padding: '18px 22px', color: 'var(--fg-muted)', fontSize: 13 }}>{t('team.empty.invites')}</div>
                  ) : invitations.map(invitation => (
                    <div key={invitation.id} style={{ display: 'grid', gap: 12, padding: 16, borderTop: '1px solid var(--border-subtle)' }}>
                      <div style={{ display: 'flex', gap: 12, alignItems: 'center', minWidth: 0 }}>
                        <Avatar name={invitation.fullName} size={36} />
                        <div style={TEXT_WRAP_STYLE}>
                          <div style={{ fontWeight: 700, color: 'var(--fg)' }}>{invitation.fullName}</div>
                          <div style={{ fontSize: 12, color: 'var(--fg-subtle)', overflowWrap: 'anywhere' }}>{invitation.email}</div>
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: MOBILE_SINGLE_COLUMN, gap: 10 }}>
                        <MobileMeta label={t('team.table.status')}>
                          <Pill tone="warning" dot size="sm">{t('team.status.pending')}</Pill>
                        </MobileMeta>
                        <MobileMeta label={t('team.table.expires')}>
                          {formatDate(invitation.expiresAt, i18n.language, emptyValue)}
                        </MobileMeta>
                        <MobileMeta label={t('team.table.invitedBy')}>
                          {invitation.invitedByFullName || emptyValue}
                        </MobileMeta>
                      </div>
                      <Button kind="secondary" size="xs" icon={I.x} disabled={actionBusyId === `invite-${invitation.id}`} onClick={() => void handleCancel(invitation)} style={mobileButtonStyle(isMobile)}>
                        {t('team.actions.cancelInvite')}
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: 'var(--ink-50)' }}>
                      {[t('team.table.member'), t('team.table.status'), t('team.table.expires'), t('team.table.invitedBy'), t('team.table.actions')].map(header => (
                        <th key={header} style={{ textAlign: 'start', padding: '11px 22px', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-subtle)' }}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {invitations.length === 0 ? (
                      <tr>
                        <td colSpan={5} style={{ padding: '18px 22px', color: 'var(--fg-muted)' }}>{t('team.empty.invites')}</td>
                      </tr>
                    ) : invitations.map(invitation => (
                      <tr key={invitation.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                        <td style={{ padding: '14px 22px' }}>
                          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                            <Avatar name={invitation.fullName} size={32} />
                            <div>
                              <div style={{ fontWeight: 600, color: 'var(--fg)' }}>{invitation.fullName}</div>
                              <div style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>{invitation.email}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: '14px 22px' }}>
                          <Pill tone="warning" dot size="sm">{t('team.status.pending')}</Pill>
                        </td>
                        <td style={{ padding: '14px 22px', color: 'var(--fg-muted)', fontSize: 12.5 }}>
                          {formatDate(invitation.expiresAt, i18n.language, emptyValue)}
                        </td>
                        <td style={{ padding: '14px 22px', color: 'var(--fg-muted)', fontSize: 12.5 }}>
                          {invitation.invitedByFullName || emptyValue}
                        </td>
                        <td style={{ padding: '14px 22px', textAlign: 'end' }}>
                          <Button kind="secondary" size="xs" icon={I.x} disabled={actionBusyId === `invite-${invitation.id}`} onClick={() => void handleCancel(invitation)}>
                            {t('team.actions.cancelInvite')}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
          </section>
        </>
      )}
    </div>
  )
}
